import { Link } from "react-router";
import { ArrowRight, FileUp } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { RecordOwnerDisplay } from "@/domains/record-owners/components";
import { formatRelativeTime } from "@/shared/lib/utils";
import { openResource } from "@/domains/resources";
import type { DashboardResourceItem } from "../queries/dashboard";
import type { DashboardSectionView } from "../hooks/use-dashboard-view-preferences";
import { DashboardCarousel } from "./dashboard-carousel";
import { DashboardSectionViewToggle } from "./dashboard-section-view-toggle";

type DashboardResourcesSectionProps = {
  items: DashboardResourceItem[];
  view: DashboardSectionView;
  onViewChange: (value: DashboardSectionView) => void;
};

export function DashboardResourcesSection({
  items,
  view,
  onViewChange,
}: DashboardResourcesSectionProps) {
  return (
    <Card className="overflow-hidden border-border/60 bg-card/95 shadow-xs">
      <div className="flex flex-col gap-4 border-b border-border/70 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            Resources
          </h2>
          <p className="text-sm text-muted-foreground">
            Most recently uploaded
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DashboardSectionViewToggle value={view} onChange={onViewChange} />
          <Button asChild className="rounded-xl px-4">
            <Link to="/resources/new">
              <FileUp className="size-4" />
              Upload resource
            </Link>
          </Button>
          <Button
            asChild
            variant="ghost"
            className="rounded-xl px-2 text-primary"
          >
            <Link to="/resources">
              View all
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-start gap-4 px-6 py-8">
          <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-4 text-muted-foreground">
            No recent resources yet.
          </div>
          <Button asChild className="rounded-xl px-4">
            <Link to="/resources/new">
              <FileUp className="size-4" />
              Upload resource
            </Link>
          </Button>
        </div>
      ) : view === "table" ? (
        <div className="overflow-x-auto px-3 py-2">
          <table className="min-w-full border-separate border-spacing-y-2">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium">Uploaded by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer rounded-2xl border border-border/60 bg-background transition-colors hover:bg-muted/30"
                  onClick={() => void openResource(item)}
                >
                  <td className="rounded-l-2xl px-3 py-3 text-sm font-medium text-foreground">
                    {item.name}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    <Badge variant="outline" className="rounded-full">
                      {item.type}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {formatRelativeTime(item.updatedAt)}
                  </td>
                  <td className="rounded-r-2xl px-3 py-3">
                    <RecordOwnerDisplay owner={item.owner} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <DashboardCarousel label="Resources">
          {items.map((item) => (
            <Card
              key={item.id}
              className="flex min-h-56 min-w-0 flex-col justify-between rounded-3xl border-border/70 bg-linear-to-br from-background to-muted/30 p-5"
            >
              <div className="space-y-4">
                <div>
                  <p className="line-clamp-2 text-lg font-semibold text-foreground">
                    {item.name}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.type} • {formatRelativeTime(item.updatedAt)}
                  </p>
                </div>
                <RecordOwnerDisplay owner={item.owner} />
              </div>

              <Button
                className="mt-6 w-full rounded-xl"
                onClick={() => void openResource(item)}
              >
                Open
              </Button>
            </Card>
          ))}
        </DashboardCarousel>
      )}
    </Card>
  );
}
